import { Button, ButtonSizeT, ButtonVariantT } from '@/components/ui/Button.tsx'
import { Loader2, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { forwardRef, ReactNode } from 'react'
import { cn } from '@/lib/utils.ts'
import PropertyPopoverTriggerTipWrapper from '@/components/common/propertyPopoverOverlay/PropertyPopoverTriggerWithTip.tsx'

type PropertyPopoverTriggerT = {
  disabled?: boolean
  loading?: boolean
  className?: string
  labelClassName?: string
  variant?: ButtonVariantT
  size?: ButtonSizeT
  label?: string | ReactNode
  icon?: ReactNode
  dataCy?: string
  handleClick?: () => void
  asDiv?: boolean
  children?: ReactNode
  error?: boolean
  showDisabledTip?: boolean
  debug?: boolean
  cl?: string
  tipContent?: ReactNode
}

export const PropertyPopoverTrigger = forwardRef<HTMLButtonElement, PropertyPopoverTriggerT>(
  (
    {
      disabled,
      loading,
      labelClassName,
      variant,
      size,
      label,
      icon,
      dataCy,
      handleClick,
      error,
      showDisabledTip,
      debug,
      className,
      cl,
      children,
      tipContent,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation()

    function onClick() {
      if (disabled) return
      handleClick && handleClick()
    }

    if (debug) {
      // console.log(props.cl)
    }

    const trigger = (
      <Button
        ref={ref}
        type="button"
        data-cy={dataCy}
        variant={variant ?? `outline`}
        size={size ?? 'sm'}
        className={cn(
          `col-span-1 flex w-fit max-w-full items-center gap-2`,
          error && `border-destructive text-destructive`,
          className,
          cl,
        )}
        disabled={loading || disabled}
        onClick={onClick}
        {...props}
      >
        {loading ? (
          <Loader2 className={'size-4 animate-spin'} />
        ) : (
          <div className={`shrink-0`}>{icon ?? <Plus className={`size-4`} />}</div>
        )}
        {label !== '' && (
          <span className={cn(`popover-trigger inline-block min-w-0 truncate`, labelClassName)}>
            {label ?? t(`common.add`)}
          </span>
        )}
        {children}
      </Button>
    )

    if (showDisabledTip)
      return (
        <PropertyPopoverTriggerTipWrapper showTip={true} content={tipContent} className={`w-full`}>
          {trigger}
        </PropertyPopoverTriggerTipWrapper>
      )

    return trigger
  },
)

PropertyPopoverTrigger.displayName = 'PropertyPopoverTrigger'
export default PropertyPopoverTrigger
